import { html, nothing } from "lit";
import type { CSSResultGroup } from "lit";

import { customElement, property, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { switchStyles } from "./switch.css.ts";

import "../field-wrapper/field-wrapper.ts";
import { ToggleBase } from "../toggle-base/toggle-base.ts";

@customElement("wow-switch")
export class WowSwitch extends ToggleBase {
  static override styles: CSSResultGroup = [switchStyles];

  @query("input") private inputEl!: HTMLInputElement;

  @property() label = "";
  @property({ attribute: "help-text" }) helpText = "";
  @property() override title = "";
  @property({ type: Boolean, reflect: true }) required = false;

  public override focus() {
    this.inputEl?.focus();
  }

  public setError(msg: string) {
    this._internals.setValidity({ customError: true }, msg, this.inputEl);
  }

  public clearError() {
    this._internals.setValidity({});
  }

  private handleChange = (e: Event): void => {
    const el = e.target as HTMLInputElement;
    this.updateStateFromEvent(el);
    this.value = el.checked.toString();

    if (el.checkValidity()) {
      this.clearError();
    } else {
      this.setError(el.validationMessage);
    }

    // Re-emit custom event that can bubble to parent components
    this.dispatchEvent(
      new CustomEvent("change", {
        bubbles: true,
        composed: true,
        detail: { checked: this.checked, value: this.value },
      }),
    );
  };

  override render() {
    return html`
      <div class="switch-container">
        ${this.label ? html`<div class="label" id="label-${this.id}">${this.label}</div>` : nothing}

        <div class="switch-wrapper">
          <span class="fake-switch">
            <input
              id=${this.id}
              part="switch"
              type="checkbox"
              name=${this.name}
              title=${this.title}
              aria-labelledby=${this.label ? `label-${this.id}` : nothing}
              ?disabled=${this.disabled}
              ?required=${this.required && !!this.name}
              .checked=${this.checked}
              class=${classMap({ switch: true, invalid: !!this.validationMessage })}
              @change=${this.handleChange}
            />
          </span>
        </div>

        ${this.helpText ? html`<div class="description">${this.helpText}</div>` : nothing}
        ${this.validationMessage ? html`<div class="error-message">${this.validationMessage}</div>` : nothing}
      </div>
    `;
  }
}
